import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from 'services/api';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface EventInstance {
  event_instance_id: number;
  event_id: number;
  created_at: string;
  type: string;
  raised: boolean;
  raiser_id: number | null;
  raiser_comment?: string | null;
  clearer_ids: Array<number | string>;
  clearer_node_instance_ids: Array<number | string>;
  clearer_user_ids: Array<number | string>;
  clearer_usernames: string[];
  cleared_at: string | null;
}

interface HeaderConfig {
  header_key: string;
  header_type: string;
  label: string;
}

interface EventInstanceState {
  items: EventInstance[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  config: HeaderConfig[];
  filtersConfig: FilterConfig[];
  oldestRaised: EventInstance | null;
  raisedCount: number;
  oldestRaisedKey: string | null;
  oldestRaisedError: boolean;
}

const initialState: EventInstanceState = {
  items: [],
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 100,
  status: 'idle',
  error: null,
  config: [],
  filtersConfig: [],
  oldestRaised: null,
  raisedCount: 0,
  oldestRaisedKey: null,
  oldestRaisedError: false,
};

export const fetchEventInstances = createAsyncThunk(
  'eventInstances/fetchEventInstances',
  async (
    {
      zoneName,
      eventName,
      page = 1,
      limit = 100,
      created_at,
      cleared_at,
      ...filters
    }: {
      zoneName: string;
      eventName: string;
      page?: number;
      limit?: number;
      created_at?: { startDate?: string; endDate?: string };
      cleared_at?: { startDate?: string; endDate?: string };
      [key: string]: any;
    },
    thunkAPI
  ) => {
    try {
      const response = await api.get(`/event_instance/${zoneName}/${eventName}`, {
        params: {
          page,
          limit,
          created_at_from: created_at?.startDate || undefined,
          created_at_to: created_at?.endDate || undefined,
          cleared_at_from: cleared_at?.startDate || undefined,
          cleared_at_to: cleared_at?.endDate || undefined,
          ...filters,
        },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch event instances');
    }
  }
);

export const fetchOldestRaisedEventInstance = createAsyncThunk(
  'eventInstances/fetchOldestRaisedEventInstance',
  async ({ zoneName, eventName }: { zoneName: string; eventName: string }, thunkAPI) => {
    try {
      const response = await api.get(`/event_instance/${encodeURIComponent(zoneName)}/${encodeURIComponent(eventName)}`, {
        params: {
          page: 1,
          limit: 1,
          raised: 'true',
          sortColumn: 'created_at',
          sortOrder: 'ASC',
        },
      });
      const { event_instances, pagination } = response.data;
      return {
        oldest: (event_instances && event_instances[0]) || null,
        raisedCount: pagination?.total_records ?? 0,
        key: `${zoneName}/${eventName}`,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch oldest raised event instance');
    }
  }
);

const eventInstanceSlice = createSlice({
  name: 'eventInstances',
  initialState,
  reducers: {
    setPage(state, action) {
      state.currentPage = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventInstances.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEventInstances.fulfilled, (state, action) => {
        const { event_instances, pagination, config, filtersConfig } = action.payload;
        state.items = event_instances || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];
        state.currentPage = pagination?.current_page ?? state.currentPage;
        state.totalPages = pagination?.total_pages ?? state.totalPages;
        state.totalItems = pagination?.total_records ?? state.totalItems;
        state.pageSize = pagination?.limit ?? state.pageSize;
        state.status = 'succeeded';
      })
      .addCase(fetchEventInstances.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      })
      .addCase(fetchOldestRaisedEventInstance.fulfilled, (state, action) => {
        state.oldestRaised = action.payload.oldest;
        state.raisedCount = action.payload.raisedCount;
        state.oldestRaisedKey = action.payload.key;
        state.oldestRaisedError = false;
      })
      .addCase(fetchOldestRaisedEventInstance.rejected, (state) => {
        state.oldestRaised = null;
        state.raisedCount = 0;
        state.oldestRaisedKey = null;
        state.oldestRaisedError = true;
      });
  },
});

export const { setPage, setPageSize } = eventInstanceSlice.actions;
export default eventInstanceSlice.reducer;
